import { Action } from "redux";
import _ from "underscore";

import { createEnterHookPromise } from "../../shared/flux/actions/enterHooks";
import { CollectionKeyHelper } from "../../shared/helpers/contentCollectionHelpers";
import { ActionPromises } from "./actionPromises";

const getInspirationFilterSetFromQS = (qs: any = {}): IListContentParams & { page: number } => {
  const parseParam = (key: string) => {
    const param = qs[key];
    return param != null
      ? param.toString().split(",").map(x => parseInt(x, 10)).sort((a, b) => a - b)
      : [];
  };
  // console.log("Parsing qs:", qs); // on SSR I saw qs like: { '?types': '2', locations: '...', ... }, needs further investigation.
  return {
    themes: parseParam("themes"),
    locations: parseParam("locations"),
    months: parseParam("months"),
    statuses: parseParam("statuses"),
    types: parseParam("types"),
    page: parseInt(qs.page, 10) || 1,
  };
};

export class EnterHookPromises {
  public static imageByFileName(paramName: string = "fileName") {
    return createEnterHookPromise((n) => ActionPromises.imageByFileName(n.params[paramName]));
  }

  public static allEnquiriesForCustomer() {
    return createEnterHookPromise(() => ActionPromises.allEnquiriesForCustomer());
  }

  public static allCustomerCurrencies = () => createEnterHookPromise(
    (router, state: IWebStoreState) => !state.currencies || !state.currencies.data ? ActionPromises.allCustomerCurrencies() : null,
  )

  public static allRegions = () => createEnterHookPromise((router, state: IWebStoreState) => !state.regions || !state.regions.data ? ActionPromises.allRegions() : null);

  public static allCountries = () => createEnterHookPromise((router, state: IWebStoreState) => !state.countries || !state.countries.data ? ActionPromises.allCountries() : null);

  public static allAgeRanges = () => createEnterHookPromise((router, state: IWebStoreState) => !state.ageRanges || !state.ageRanges.data ? ActionPromises.allAgeRanges() : null);

  public static teamProfiles() {
    return createEnterHookPromise(() => ActionPromises.teamProfiles());
  }

  public static getContentPageFromQuery = (defaultPageSize: number) => createEnterHookPromise((router, state: IWebStoreState) => {
    const actions = [];
    const fs = getInspirationFilterSetFromQS(router.location.query);
    const inspirationState = state.contentByCollection.data.inspiration;

    let pageSize = defaultPageSize;
    // no need to reload state if the store is the same as the query string.
    if (!inspirationState || inspirationState.context == null || !_.isEqual(fs, { ...(inspirationState.context.params || {}), page: inspirationState.context.page })) {
      // Try get all items in the first page - for server render
      if (fs.page > 0 && fs.page < 12 /* safety measure */) {
        pageSize = fs.page * defaultPageSize;
        fs.page = -1; // reset the store
      }

      const { page, ...filters } = fs;
      actions.push(ActionPromises.getContentPage(filters, page, pageSize, { key: CollectionKeyHelper.Inspiration }));
    }

    // to handle secondary themes, TODO guarantee this runs after contentThemes enterhook
    const requiredThemes = _.without(fs.themes, ...state.contentThemes.data.map(x => x.id));
    actions.push(...(requiredThemes.map(id => ActionPromises.getTheme(id))));

    return actions;
  })

  public static contentThemes = (count: number) => createEnterHookPromise(
    (router, state: IWebStoreState) => !state.contentThemes || !_.any(state.contentThemes.data) ? ActionPromises.inspirationThemes(count) : null,
  )

  public static enquiryThemes = (count: number) => createEnterHookPromise(
    (router, state: IWebStoreState) => !state.enquiryThemes || !_.any(state.enquiryThemes.data) ? ActionPromises.enquiryThemes(count) : null,
  )

  public static allCardTypes = () => createEnterHookPromise(
    (router, state: IWebStoreState) => !state.cardTypes.data || !state.cardTypes.data.length ? ActionPromises.allCardTypes() : null,
  )

  public static destinationBySlug(paramName: string) {
    return createEnterHookPromise((n) => ActionPromises.destinationBySlug(n.params[paramName]));
  }

  public static customerEnquiryData(paramName: string = "enquiryId", updateEnqItin?: boolean) {
    return createEnterHookPromise((n) => ActionPromises.customerEnquiry(parseInt(n.params[paramName], 10), updateEnqItin));
  }

  public static ltoBySlug(paramName: string = "ltoSlug") {
    return createEnterHookPromise((router, state: IWebStoreState) => {
      const routeSlug = router.params[paramName];
      if (!state.lto.data || state.lto.data.slug !== routeSlug) {
        return ActionPromises.ltoByIdOrSlug(routeSlug);
      }
    });
  }

  public static ltoTermsBySlug(paramName: string = "ltoSlug") {
    return createEnterHookPromise((n) => ActionPromises.ltoTermsBySlug(n.params[paramName]));
  }

  public static ltosByLocationSlug(paramName: string) {
    return createEnterHookPromise((n) => ActionPromises.ltosByLocationSlug(n.params[paramName]));
  }

  public static destinationsByCountrySlug(paramName: string) {
    return createEnterHookPromise((n) => ActionPromises.destinationsByCountrySlug(n.params[paramName]));
  }

  public static countriesByRegionSlug(paramName: string = "regionSlug", isPrimary?: boolean) {
    return createEnterHookPromise((n) => ActionPromises.countriesByRegionId(n.params[paramName], isPrimary));
  }

  public static sampleItineraryById(paramName: string = "itineraryId") {
    return createEnterHookPromise((router, state: IWebStoreState) => ActionPromises.sampleItineraryById(router.params[paramName], state?.currency?.data?.code || "GBP"));
  }

  public static enquiryById(paramName: string = "enquiryId") {
    return createEnterHookPromise((n) => ActionPromises.enquiryById(parseInt(n.params[paramName], 10)));
  }

  public static messagesByEnquiryId(paramName: string = "enquiryId") {
    return createEnterHookPromise((n) => ActionPromises.enquiryMessagesByEnquiryId(parseInt(n.params[paramName], 10)));
  }

  public static paymentsByEnquiryId(paramName: string = "enquiryId") {
    return createEnterHookPromise((n) => ActionPromises.enquiryPaymentsByEnquiryId(parseInt(n.params[paramName], 10), [PaymentStatus.Done, PaymentStatus.Pending]));
  }

  public static logoutIfRequested() {
    return createEnterHookPromise((route) => {
      if (route.location.query?.loggedOutReason) {
        return ActionPromises.logout();
      }
    });
  }

  public static enquiryItineraryFromRoute() {
    return createEnterHookPromise<IWebStoreState>((route, state) => {
      let quoteId: number = null;
      const enquiry = state.customerEnquiry.data;

      if (route.params.itineraryQuoteId != null) {
        // Handle /:enquiryId/itinerary/:itineraryId
        const routeQuoteId = parseInt(route.params.itineraryQuoteId, 10);
        if (routeQuoteId && !isNaN(routeQuoteId)) {
          quoteId = routeQuoteId;
        }
      }
      else if (enquiry != null && enquiry.isConnected && enquiry.currentQuoteId != null) {
        // Handle /:enquiryId/itinerary, /:enquiryId/payment
        quoteId = enquiry.currentQuoteId;
      }

      if (quoteId) {
        return ActionPromises.enquiryItineraryQuoteById(quoteId);
      }
    });
  }
}
